import { apiClient } from './api';

declare global {
  interface Window {
    fbq?: (
      action: 'track' | 'trackCustom',
      event: string,
      params?: Record<string, unknown>,
      options?: { eventID?: string }
    ) => void;
    ttq?: {
      track: (event: string, params?: Record<string, unknown>) => void;
      page: () => void;
      identify: (params?: Record<string, unknown>) => void;
    };
    gtag?: (...args: unknown[]) => void;
  }
}

export const PIXEL_EVENTS = {
  COMPLETE_REGISTRATION: 'CompleteRegistration',
  VIEW_CONTENT: 'ViewContent',
  INITIATE_CHECKOUT: 'InitiateCheckout',
  PURCHASE: 'Purchase',
  LEAD: 'Lead',
  ADD_TO_CART: 'AddToCart',
} as const;

// Meta usa shape flat (content_ids: string[], content_type, content_name).
// TikTok exige array `contents: [{ content_id, content_type, content_name }]`.
// Esta função traduz o payload do callsite para o formato esperado pelo ttq.
function toTikTokParams(params?: Record<string, unknown>): Record<string, unknown> | undefined {
  if (!params) return undefined;
  const { content_ids, content_type, content_name, content_category, value, currency, ...rest } =
    params as {
      content_ids?: string[];
      content_type?: string;
      content_name?: string;
      content_category?: string;
      value?: number;
      currency?: string;
    } & Record<string, unknown>;

  const itemName = content_name ?? content_category;
  const out: Record<string, unknown> = { ...rest };

  if (Array.isArray(content_ids) && content_ids.length > 0) {
    out.contents = content_ids.map((id) => ({
      content_id: id,
      ...(content_type ? { content_type } : {}),
      ...(itemName ? { content_name: itemName } : {}),
    }));
  } else if (itemName || content_type) {
    out.contents = [
      {
        ...(content_type ? { content_type } : {}),
        ...(itemName ? { content_name: itemName } : {}),
      },
    ];
  }

  if (value !== undefined) out.value = value;
  if (currency !== undefined) out.currency = currency;

  return out;
}

// ---------------------------------------------------------------------------
// Buffer de eventos pré-load.
//
// Pixels (Meta + TikTok) carregam diferidos no `window.load` event para não
// bloquear LCP. Em páginas como /assinatura?success=true (retorno do Stripe),
// o useEffect de Purchase roda no mount — antes de `load` disparar — quando
// window.fbq e window.ttq ainda não existem. Sem buffer, o evento é perdido.
// ---------------------------------------------------------------------------

type PendingTrack = {
  event: string;
  params?: Record<string, unknown>;
  ttParams?: Record<string, unknown>;
  eventId?: string;
  needsFb: boolean;
  needsTt: boolean;
};

const MAX_PENDING_TRACKS = 20;
const pendingTracks: PendingTrack[] = [];
let flushListenerWired = false;

function flushPendingTracks(): void {
  if (typeof window === 'undefined') return;
  for (let i = pendingTracks.length - 1; i >= 0; i--) {
    const item = pendingTracks[i];
    if (item.needsFb && typeof window.fbq === 'function') {
      try {
        window.fbq('track', item.event, item.params, item.eventId ? { eventID: item.eventId } : undefined);
      } catch {
        // analytics nunca quebra UX
      }
      item.needsFb = false;
    }
    if (item.needsTt && window.ttq && typeof window.ttq.track === 'function') {
      try {
        window.ttq.track(item.event, item.ttParams);
      } catch {
        // analytics nunca quebra UX
      }
      item.needsTt = false;
    }
    if (!item.needsFb && !item.needsTt) {
      pendingTracks.splice(i, 1);
    }
  }
}

function wireFlushListener(): void {
  if (flushListenerWired || typeof window === 'undefined') return;
  flushListenerWired = true;
  // Pixel snippets rodam em window.load + setTimeout(0); aguardar tick extra
  // pra garantir que fbq/ttq foram criados como stubs antes de drenar.
  const onLoad = () => setTimeout(flushPendingTracks, 50);
  if (document.readyState === 'complete') {
    onLoad();
  } else {
    window.addEventListener('load', onLoad, { once: true });
  }
}

// ---------------------------------------------------------------------------
// Conversions API (CAPI) — espelho server-side.
//
// O mesmo evento disparado no pixel do browser é enviado ao backend, que o
// reenvia à Meta com IP/user-agent/email hasheado. O `eventID` é compartilhado
// entre os dois caminhos: é o que permite a Meta deduplicar (não conta a
// conversão duas vezes). Cookies _fbp/_fbc são lidos aqui e repassados para
// melhorar o match. Tudo fire-and-forget: tracking nunca bloqueia nem quebra UX.
// ---------------------------------------------------------------------------

function newEventId(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch {
    // crypto indisponível — cai no fallback
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function readCookie(name: string): string | undefined {
  if (typeof document === 'undefined') return undefined;
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : undefined;
}

function sendServerEvent(event: string, eventId: string, params?: Record<string, unknown>): void {
  if (typeof window === 'undefined') return;
  const body: Record<string, unknown> = {
    eventName: event,
    eventId,
    eventSourceUrl: window.location.href,
  };
  if (params && Object.keys(params).length > 0) body.customData = params;
  const fbp = readCookie('_fbp');
  const fbc = readCookie('_fbc');
  if (fbp) body.fbp = fbp;
  if (fbc) body.fbc = fbc;

  // keepalive permite o POST sobreviver à navegação (ex.: clique em CTA que
  // troca de página logo após o trackEvent).
  apiClient('/track/conversion', {
    method: 'POST',
    body: JSON.stringify(body),
    keepalive: true,
  }).catch(() => {
    // analytics nunca quebra UX
  });
}

export function trackEvent(event: string, params?: Record<string, unknown>): void {
  if (typeof window === 'undefined') return;
  const ttParams = toTikTokParams(params);
  const eventId = newEventId();
  let needsFb = false;
  let needsTt = false;

  try {
    if (typeof window.fbq === 'function') {
      window.fbq('track', event, params, { eventID: eventId });
    } else {
      needsFb = true;
    }
  } catch {
    // analytics nunca quebra UX
  }
  try {
    if (window.ttq && typeof window.ttq.track === 'function') {
      window.ttq.track(event, ttParams);
    } else {
      needsTt = true;
    }
  } catch {
    // analytics nunca quebra UX
  }

  // CAPI server-side: sempre dispara (independe de o pixel do browser ter
  // carregado), com o mesmo eventId para deduplicação.
  sendServerEvent(event, eventId, params);

  if (!needsFb && !needsTt) return;
  if (pendingTracks.length >= MAX_PENDING_TRACKS) return;
  pendingTracks.push({ event, params, ttParams, eventId, needsFb, needsTt });
  wireFlushListener();
}

// Dispara um evento APENAS no pixel do TikTok (não no Meta). Reusa o mesmo buffer
// pré-load (needsFb: false). Usado para sinais que só interessam ao TikTok.
export function trackTikTok(event: string, params?: Record<string, unknown>): void {
  if (typeof window === 'undefined') return;
  const ttParams = toTikTokParams(params);

  try {
    if (window.ttq && typeof window.ttq.track === 'function') {
      window.ttq.track(event, ttParams);
      return;
    }
  } catch {
    // analytics nunca quebra UX
  }

  if (pendingTracks.length >= MAX_PENDING_TRACKS) return;
  pendingTracks.push({ event, params, ttParams, needsFb: false, needsTt: true });
  wireFlushListener();
}

// ---------------------------------------------------------------------------
// Atribuição TikTok — marca first-touch quando a visita vem de anúncio do TikTok
// (param `ttclid` auto-anexado pelo TikTok, ou utm_source=tiktok). Persiste em
// localStorage pra sobreviver à navegação SPA até o momento do cadastro.
// ---------------------------------------------------------------------------

const TIKTOK_ATTR_KEY = 'tt_attributed';
const TIKTOK_CLICK_ID_KEY = 'tt_ttclid';

export function captureTikTokAttribution(): void {
  if (typeof window === 'undefined') return;
  try {
    if (localStorage.getItem(TIKTOK_ATTR_KEY)) return;
    const params = new URLSearchParams(window.location.search);
    const ttclid = params.get('ttclid');
    const source = (params.get('utm_source') ?? '').toLowerCase();
    if (ttclid || source.includes('tiktok')) {
      localStorage.setItem(TIKTOK_ATTR_KEY, '1');
      if (ttclid) localStorage.setItem(TIKTOK_CLICK_ID_KEY, ttclid);
    }
  } catch {
    // localStorage pode lançar em modo privado
  }
}

export function cameFromTikTok(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return localStorage.getItem(TIKTOK_ATTR_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * Params de atribuição TikTok pra reanexar a uma URL que cruza contexto/browser
 * (ex.: continue URL do link de email, aberto em outro navegador onde o
 * localStorage não existe). A recaptura no destino restaura `tt_attributed`.
 * Vazio se a visita não veio do TikTok.
 */
export function getTikTokAttributionParams(): Record<string, string> {
  if (!cameFromTikTok()) return {};
  const out: Record<string, string> = { utm_source: 'tiktok' };
  try {
    const ttclid = localStorage.getItem(TIKTOK_CLICK_ID_KEY);
    if (ttclid) out.ttclid = ttclid;
  } catch {
    // ignore
  }
  return out;
}

// ---------------------------------------------------------------------------
// Google Ads — conversão "Purchase".
//
// O gtag base e a conversão de page-view carregam síncronos no <head>
// (index.html), então window.gtag já existe quando o useEffect de sucesso roda
// no retorno do Stripe — sem necessidade de buffer como os pixels diferidos.
// Guard defensivo mesmo assim: analytics nunca quebra UX.
//
// transaction_id (Stripe session id) deduplica conversões em reloads da página
// de sucesso — o Google Ads ignora disparos repetidos com o mesmo id.
// ---------------------------------------------------------------------------

const GOOGLE_ADS_PURCHASE_SEND_TO = 'AW-17529641634/481CCKap0LgcEKK15KZB';

export function trackGoogleAdsPurchase(opts?: {
  value?: number;
  currency?: string;
  transactionId?: string;
}): void {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
  try {
    window.gtag('event', 'conversion', {
      send_to: GOOGLE_ADS_PURCHASE_SEND_TO,
      ...(opts?.value !== undefined ? { value: opts.value } : {}),
      ...(opts?.currency ? { currency: opts.currency } : {}),
      ...(opts?.transactionId ? { transaction_id: opts.transactionId } : {}),
    });
  } catch {
    // analytics nunca quebra UX
  }
}

// ---------------------------------------------------------------------------
// Escada de funil — reaproveita a ação de conversão de compra (decisão: aceitar
// relatório "sujo" no Google Ads; receita real é medida no Stripe). Cada degrau
// dispara a MESMA conversão com valor crescente. Para o algoritmo otimizar quem
// sobe a escada, exige lance "Maximizar valor da conversão" e a ação de compra
// com Contagem="Todas" e Valor="Usar valores diferentes para cada conversão".
// Ajuste os pesos abaixo conforme a economia real do funil.
// ---------------------------------------------------------------------------

const FUNNEL_STEP_VALUES = {
  engaged_view: 1,
  login_view: 10,
  sign_up: 50,
  begin_checkout: 100,
} as const;

export type FunnelStep = keyof typeof FUNNEL_STEP_VALUES;

export function trackFunnelSignal(step: FunnelStep): void {
  trackGoogleAdsPurchase({ value: FUNNEL_STEP_VALUES[step], currency: 'BRL' });
}

// ---------------------------------------------------------------------------
// TikTok identify — envia external_id (Firebase UID) hasheado em SHA-256.
// ---------------------------------------------------------------------------

async function sha256Hex(input: string): Promise<string> {
  const buf = new TextEncoder().encode(input);
  const hash = await crypto.subtle.digest('SHA-256', buf);
  return Array.from(new Uint8Array(hash))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

let pendingTikTokExternalId: string | null = null;
let identifyListenerWired = false;

function flushTikTokIdentify(): void {
  if (typeof window === 'undefined' || !pendingTikTokExternalId) return;
  if (!window.ttq?.identify) return;
  window.ttq.identify({ external_id: pendingTikTokExternalId });
  pendingTikTokExternalId = null;
}

/**
 * Identifica o usuário no pixel do TikTok com external_id = SHA-256(firebaseUid).
 * Sem email/telefone (PII). Se o pixel ainda não carregou (carregamento diferido
 * ao load event), enfileira e tenta de novo após `load`.
 */
export async function identifyUser(externalId: string): Promise<void> {
  if (typeof window === 'undefined' || !externalId) return;
  try {
    pendingTikTokExternalId = await sha256Hex(externalId);
    flushTikTokIdentify();
    if (pendingTikTokExternalId === null || identifyListenerWired) return;
    identifyListenerWired = true;
    // Pixel é deferido ao window.load + setTimeout(0); aguardar tick após load.
    const onLoad = () => setTimeout(flushTikTokIdentify, 50);
    if (document.readyState === 'complete') {
      onLoad();
    } else {
      window.addEventListener('load', onLoad, { once: true });
    }
  } catch {
    // analytics nunca quebra UX
  }
}

export interface PendingPurchase {
  contentType: 'subscription' | 'topup';
  contentIds: string[];
  value: number;
  currency: 'BRL';
}

const PENDING_KEY = 'meta_pixel_pending_purchase';

export function setPendingPurchase(payload: PendingPurchase): void {
  try {
    sessionStorage.setItem(PENDING_KEY, JSON.stringify(payload));
  } catch {
    // sessionStorage pode lançar em Safari privado
  }
}

export function consumePendingPurchase(): PendingPurchase | null {
  try {
    const raw = sessionStorage.getItem(PENDING_KEY);
    if (!raw) return null;
    sessionStorage.removeItem(PENDING_KEY);
    return JSON.parse(raw) as PendingPurchase;
  } catch {
    return null;
  }
}
